import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Stack,
  Checkbox,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from "@mui/material";
// Icons
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from '@mui/icons-material/Add';
import { pageVariants, pageTransition } from "../../constants/GlobalStyles";
import { motion } from 'framer-motion';

const PendingEncounterDetails = ({ encounter, handleDispenseDrugs, handleDone }) => {
  const [selectedDrugs, setSelectedDrugs] = useState([]);
  const [openDispenseDialog, setOpenDispenseDialog] = useState(false);
  const [openDoneDialog, setOpenDoneDialog] = useState(false);
  

  const [encounterNote, setEncounterNote] = useState("");

  const statusLabel = {
    approved: "Approved",
    rejected: "Rejected",
  }

  const handleToggleDrug = (drugObj) => {
    setSelectedDrugs((preV) => {
      const foundDrug = preV.find((drug) => drug.encounter_drug_id === drugObj.id);

      if (foundDrug) {
        return preV.filter((drug) => drug.encounter_drug_id !== drugObj.id);
      } else {
        return [...preV, {
          "encounter_drug_id": drugObj.id,
          "quantity": drugObj.claimed_quantity
        }];
      }
    })

  };


  useEffect(() => {
    setSelectedDrugs([]);
    setEncounterNote("");
  }, [encounter]);

  const undispensedDrugs = encounter.encounterdrugs.filter(
    (drug) =>
      !drug.is_dispensed
  )

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      key={encounter.id}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          fontFamily: "'Inter', sans-serif"
        }}
      >
        {/* HEADER SECTION */}
        <Box sx={{ p: 3, bgcolor: "#f0fdfa" }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: "#111827" }}>
            {encounter.patient.name}
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 18, color: "#10b981" }} />
              <Typography fontWeight={500} fontSize={16} color="#4b5563">
                {new Date(encounter.date).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                }).replace(",", " •")}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonOutlineIcon sx={{ fontSize: 18, color: "#10b981" }} />
              <Typography fontWeight={500} fontSize={16} color="#4b5563">
                Dr. {encounter.provider.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentOutlinedIcon sx={{ fontSize: 18, color: "#10b981" }} />
              <Typography fontSize={16} fontWeight={500} color="#4b5563">
                Prescription ID: {encounter.reference_id}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ICDs section */}
        <Box sx={{ p: 1, borderBottom: '1px solid #f3f4f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, }}>
            <LocalHospitalIcon sx={{ fontSize: 20, color: "#64748B" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#475569", letterSpacing: '0.025em' }}>
              DIAGNOSIS (ICD CODES)
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {encounter.encountericds?.map((icd) => (
              <Grid item xs={12} sm={6} key={icd.id}>
                <Box sx={{ 
                  py: 1, 
                  px: 3, 
                  border: '1px solid #f1f5f9', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  bgcolor: '#FBFDFF'
                }}>
                  <Box>
                    <Typography component="div" sx={{fontWeight: 600, fontSize: "0.85rem", color: "#64748B", letterSpacing: '0.05em' }}>
                      {icd.icd} {icd.primary && <Chip label="Primary" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: '#e0f2fe', color: '#0369a1' }} />}
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                      {icd.shortdescription}
                    </Typography>
                  </Box>
                  <Chip
                    label={icd.actionmsg?.status?.state || 'Pending'}
                    size="small"
                    sx={{ 
                      fontWeight: 600, 
                      textTransform: 'capitalize',
                      bgcolor: icd.actionmsg?.status?.state === 'approved' ? "#dcfce7" : '#fee2e2', 
                      color: icd.actionmsg?.status?.state === 'approved' ? "#166534" : '#b91c1c' 
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* DRUGs section */}
        {/* COLUMN HEADERS */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 1, my: 1.5 }}>
          <MedicalServicesIcon sx={{ fontSize: 20, color: "#64748B" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#475569", letterSpacing: '0.025em' }}>
            MEDICATIONS
          </Typography>
        </Box>
        
        {/* HEADER SECTION - Added pr: 5.5 to match the Accordion's expand icon space */}
        <Box sx={{ m: 1, px: 3, py: 2, pr: 5.5, borderBottom: '1px solid #f3f4f6', bgcolor: '#F8FAFC' }}>
          <Grid container sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#64748B", letterSpacing: '0.05em' }}>
            <Grid item xs={1.5} sx={{ textAlign: "center" }}>DISPENSE</Grid>
            <Grid item xs={3.5} sx={{ textAlign: "center" }}>GENERIC NAME</Grid>
            <Grid item xs={2} sx={{ textAlign: "center" }}>DISPENSED</Grid>
            <Grid item xs={1.5} sx={{ textAlign: "center" }}>QUANTITY</Grid>
            <Grid item xs={1.5} sx={{ textAlign: "center" }}>COST</Grid>
            <Grid item xs={2} sx={{ textAlign: "center" }}>INSURANCE STATUS</Grid>
          </Grid>
        </Box>

        {/* DRUG LIST */}
        {encounter.encounterdrugs.map((drug) => (
          <Accordion
            key={drug.id}
            elevation={0}
            disableGutters
            sx={{
              m: 1,
              "&:before": { display: "none" },
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
              sx={{ px: 3, py: 1, "& .MuiAccordionSummary-content": { margin: 0 } }}
            >
              <Grid container alignItems="center">
                <Grid item xs={1.5} sx={{ display: "flex", justifyContent: "center" }}>
                  <Checkbox 
                    checked={selectedDrugs.some(d => d.encounter_drug_id === drug.id)}
                    disabled={drug.is_dispensed || drug.in_stock === 'Out of stock' || drug.in_stock === 'Not in inventory'}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleDrug(drug);
                    }} 
                    sx={{ color: "#10b981", "&.Mui-checked": { color: "#34d399" }, p: 0 }} 
                  />
                </Grid>
                <Grid item xs={3.5} sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 500, color: drug?.actionmsg?.status?.state === 'approved' ? "#4b5563" : '#E02626' }}>
                    {drug.drug_name}
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ display: "flex", justifyContent: "center" }}>
                  <Chip
                    label={drug.is_dispensed ? 'Yes' : (drug.in_stock === 'Not in inventory' ? 'Out of inventory' : drug.in_stock === 'Out of stock' ? 'Out of stock' : 'No')}
                    sx={{ bgcolor: "#ffffffff", color: drug.is_dispensed ? '#0d9488' : (drug.in_stock === 'In stock' ? '#E02626' : '#4b5563'), fontWeight: 600, borderRadius: "4px", height: 24 }}
                  />
                </Grid>
                <Grid item xs={1.5} sx={{ display: "flex", justifyContent: "center" }}>
                  <Chip
                    label={`${drug.claimed_quantity}`}
                    sx={{ bgcolor: "#f0fdf4", color: "#166534", fontWeight: 600, borderRadius: "4px", height: 24 }}
                  />
                </Grid>

                <Grid item xs={1.5} sx={{ display: "flex", justifyContent: "center" }}>
                  <Chip
                    label={drug.pharmacy_price === null ? 'N/A' : `$ ${drug.pharmacy_price}`}
                    sx={{ bgcolor: "#f0fdf4", color: "#166534", fontWeight: 600, borderRadius: "4px", height: 24 }}
                  />
                </Grid>

                <Grid item xs={2} sx={{ display: "flex", justifyContent: "center" }}>
                  <Chip
                    label={statusLabel[drug?.actionmsg?.status?.state]}
                    sx={{ bgcolor: drug?.actionmsg?.status?.state === 'approved' ? "#dcfce7" : '#FEE2E2', color: drug?.actionmsg?.status?.state === 'approved' ? "#166534" : '#E02626', fontWeight: 600, borderRadius: "4px", height: 24 }}
                  />
                </Grid>
              </Grid>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 3, pb: 4, pt: 0 }}>
              {/* The Details Box matching the screenshot */}
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 3, mt: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Brand Name</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug?.drug_brand}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Generic Name</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug?.drug_name}</Typography>
                  </Grid>
                  

                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Frequency</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug?.frequency}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Cost</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug.pharmacy_price === null ? 'N/A' : `$ ${drug.pharmacy_price}`}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Drug Dose</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug?.drug_dose}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Duration</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug?.duration}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Claimed Quantity</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug?.claimed_quantity}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Approved Quantity</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug?.approved_quantity}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Rejected Quantity</Typography>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{drug?.rejected_quantity}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Refill Status</Typography>
                    <Box sx={{
                      bgcolor: drug?.is_active_refill ? "#dcfce7" : '#FEE2E2',
                      color: drug?.is_active_refill ? "#166534" : '#E02626',
                      p: 1.5,
                      borderRadius: "8px",
                      fontWeight: 700,
                      mt: 0.5,
                      border: '1px solid',
                      borderColor: drug?.is_active_refill ? "#bbf7d0" : '#fca5a5',
                    }}>
                      {drug?.is_active_refill ? 'active' : 'Inactive'}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 500 }}>Insurance Status</Typography>
                    <Box sx={{
                      bgcolor: drug?.actionmsg?.status?.state === 'approved' ? "#dcfce7" : '#FEE2E2',
                      color: drug?.actionmsg?.status?.state === 'approved' ? "#166534" : '#E02626',
                      p: 1.5,
                      borderRadius: "8px",
                      fontWeight: 700,
                      mt: 0.5,
                      border: '1px solid',
                      borderColor: drug?.actionmsg?.status?.state === 'approved' ? "#bbf7d0" : '#fca5a5',
                    }}>
                      {statusLabel[drug?.actionmsg?.status?.state]}
                    </Box>
                  </Grid>
                </Grid>

                {drug.dispensing_history && drug.dispensing_history.length > 0 && (
  <Box sx={{ mt: 3 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#475569" }}>
      Dispensing History
    </Typography>
    <Stack spacing={2}>
      {drug.dispensing_history.map((history) => (
        <Box key={history.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 2, bgcolor: '#f9fafb' }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "#6b7280" }}>Dispensed By</Typography>
              <Typography sx={{ fontWeight: 600 }}>{history.dispensed_by_name || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "#6b7280" }}>Dispensed At</Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {new Date(history.dispensed_at).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              }).replace(",", " •")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "#6b7280" }}>Quantity Dispensed</Typography>
              <Typography sx={{ fontWeight: 600 }}>{history.quantity_dispensed}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "#6b7280" }}>Status</Typography>
              <Chip
                label={history.is_rejected ? "Refused" : "Dispensed"}
                size="small"
                sx={{
                  bgcolor: history.is_rejected ? "#fee2e2" : "#dcfce7",
                  color: history.is_rejected ? "#b91c1c" : "#166534",
                  fontWeight: 600,
                  height: 24
                }}
              />
            </Grid>
            {history.rejection_reason && (
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: "#6b7280" }}>Rejection Reason</Typography>
                <Typography sx={{ fontWeight: 600 }}>{history.rejection_reason}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}
    </Stack>
  </Box>
)}

              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* FOOTER */}
        <Box sx={{ p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: '1px solid #f3f4f6' }}>
          <Typography component='div' sx={{ color: "#64748B", fontSize: "0.875rem", fontWeight: 600 }}>
            <span style={{ color: '#4ABBA7' }}>{selectedDrugs.length}</span> Medication(s) selected
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              // disabled={selectedDrugs.length === 0}
              sx={{
                bgcolor: "#2563eb",
                textTransform: "none",
                borderRadius: "6px",
                px: 3,
                fontWeight: 600,
                width: 'fit-content',
                "&:hover": { 
                  bgcolor: "#1d4ed8"
                },
              }}
              onClick={() => setOpenDoneDialog(true)}
            >
              Done
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={selectedDrugs.length === 0}
              sx={{
                bgcolor: "#45a291",
                textTransform: "none",
                borderRadius: "6px",
                px: 3,
                fontWeight: 600,
                width: 'fit-content',
                "&:hover": { bgcolor: "#388e7e" },
              }}
              onClick={() => setOpenDispenseDialog(true)}
            >
              Dispense
            </Button>

          </Box>
        </Box>
      </Paper>
      <Dialog open={openDispenseDialog} onClose={() => setOpenDispenseDialog(false)}>
        <DialogTitle>Confirm Dispensing</DialogTitle>

        <DialogContent>
          <Typography mb={2}>
            Are you sure you want to dispense{" "}
            <b>{selectedDrugs.length}</b> medication(s)?
          </Typography>

        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDispenseDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={() => {
              handleDispenseDrugs({
                encounter_id: encounter.id,
                selectedDrugs,
              });

              setEncounterNote("");
              setOpenDispenseDialog(false);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Done Dialog */}

      <Dialog open={openDoneDialog} onClose={() => setOpenDispenseDialog(false)}>
        <DialogTitle>Confirm</DialogTitle>

        <DialogContent>
          <Typography mb={2}>
            Are you sure you want to complete this dispense?
          </Typography>

          {/* <Box mb={2}>
            <Typography>Some prescribed medications have not been dispensed yet.</Typography>
              {encounter.encounterdrugs.filter((drug) => drug.pharmacy_price > 0).map((drug) => <Typography>{drug.drug_name}</Typography>)}
          </Box> */}

          {undispensedDrugs.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography fontWeight={500}>
              The following prescribed medications have not been dispensed:
            </Typography>

            <Box mt={1} pl={2}>
              {undispensedDrugs
                .map((drug) => (
                  <Typography
                    key={drug.drug_name}
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    • {drug.drug_name}
                  </Typography>
                ))}
            </Box>
          </Alert>}

          <TextField
            label="Encounter Note (optional)"
            placeholder={
              undispensedDrugs.length > 0
                ? "Add a note explaining why some medications were not dispensed"
                : "Add a note for this encounter"
            }
            multiline
            rows={3}
            fullWidth
            value={encounterNote}
            onChange={(e) => setEncounterNote(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDoneDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={() => {
              handleDone({
                encounter_id: encounter.id,
                notes: encounterNote,
              });

              setOpenDoneDialog(false);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>

  );
};

export default PendingEncounterDetails;